import { Switch, SwitchProps } from '@chronosphereio/chrono-ui';
import {
  isUnitWithAbbreviate,
  isUnitWithDecimalPlaces,
  MAX_DECIMAL_PLACES,
  shouldAbbreviate,
  UNIT_CONFIG,
  UnitConfig,
  UnitOptions,
} from '../../../core';
import { OptionsEditorControl } from '../OptionsEditorLayout';
import { SettingsAutocomplete } from '../SettingsAutocomplete';

export interface UnitSelectorProps {
  value: UnitOptions;
  onChange: (unit: UnitOptions) => void;
  disabled?: boolean;
}

type AutocompleteKindOption = UnitConfig & { id: UnitOptions['kind'] };

const KIND_OPTIONS: AutocompleteKindOption[] = Object.entries(UNIT_CONFIG)
  .map(([id, config]) => {
    return {
      id: id as UnitOptions['kind'],
      ...config,
    };
  })
  .filter((config) => !config.disableSelectorOption);

function getKindOption(value: UnitOptions): AutocompleteKindOption {
  const option = {
    id: value.kind,
    ...UNIT_CONFIG[value.kind],
  };

  if (value.kind === 'Custom') {
    // Include the correct label for the custom unit
    return {
      ...option,
      label: value.value ?? '',
    };
  } else {
    return option;
  }
}

export const DECIMAL_PLACES_OPTIONS = [
  { id: 'default', label: 'Default', decimal_places: undefined },
  { id: '0', label: '0', decimal_places: 0 },
  { id: '1', label: '1', decimal_places: 1 },
  { id: '2', label: '2', decimal_places: 2 },
  { id: '3', label: '3', decimal_places: 3 },
  { id: '4', label: '4', decimal_places: 4 },
  { id: 'max', label: 'Max', decimal_places: MAX_DECIMAL_PLACES },
];

function getDecimalPlacesOption(decimal_places?: number) {
  return DECIMAL_PLACES_OPTIONS.find((o) => o.decimal_places === decimal_places);
}

export function UnitSelector({ value, onChange, disabled = false }: UnitSelectorProps) {
  const hasDecimalPlaces = isUnitWithDecimalPlaces(value);
  const hasAbbreviate = isUnitWithAbbreviate(value);

  const handleKindChange = (_: unknown, option: AutocompleteKindOption) => {
    if (option.id === 'Custom') {
      // If user selected a custom unit, we need to store the custom unit in the value property
      onChange({
        kind: option.id,
        value: option.label,
      });
    } else {
      onChange({ kind: option.id });
    }
  };

  const handleDecimalPlacesChange = (_: unknown, { decimal_places }: { decimal_places: number | undefined }) => {
    if (hasDecimalPlaces) {
      onChange({
        ...value,
        decimal_places: decimal_places,
      });
    }
  };

  const handleAbbreviateChange: SwitchProps['onChange'] = (_: unknown, checked: boolean) => {
    if (hasAbbreviate) {
      onChange({
        ...value,
        abbreviate: checked,
      });
    }
  };

  return (
    <>
      <OptionsEditorControl
        label="Unit"
        control={
          <SettingsAutocomplete
            value={getKindOption(value)}
            options={KIND_OPTIONS}
            groupBy={(option) => option.group}
            onChange={handleKindChange}
            disableClearable
            disabled={disabled}
            filterOptions={(_, { inputValue }) => {
              if (!inputValue) return KIND_OPTIONS;

              // Filter to options that match what the user has typed
              const results = KIND_OPTIONS.filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase()));

              // Add a custom unit option based on what the user has typed
              results.push({
                id: 'Custom',
                ...UNIT_CONFIG['Custom'],
                label: `${inputValue}`,
                description: 'Custom unit',
              });

              return results;
            }}
          ></SettingsAutocomplete>
        }
      />
      <OptionsEditorControl
        label="Decimals"
        description="Maximum number of decimal places"
        control={
          <SettingsAutocomplete
            value={getDecimalPlacesOption(value.decimal_places)}
            options={DECIMAL_PLACES_OPTIONS}
            getOptionLabel={(o) => o.label}
            onChange={handleDecimalPlacesChange}
            disabled={!hasDecimalPlaces}
            disableClearable
          />
        }
      />
      <OptionsEditorControl
        label="Abbreviate"
        description="e.g. 1000 => 1K, 1000000 => 1M"
        control={
          <Switch
            checked={hasAbbreviate ? shouldAbbreviate(value.abbreviate) : false}
            onChange={handleAbbreviateChange}
            disabled={!hasAbbreviate}
          />
        }
      />
    </>
  );
}
